import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Cookie, X } from "lucide-react";

const CookieBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem("cookie_consent", JSON.stringify({
      accepted: true,
      date: new Date().toISOString(),
      essential: true,
      analytics: false // We don't use analytics cookies
    }));
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem("cookie_consent", JSON.stringify({
      accepted: false,
      date: new Date().toISOString(),
      essential: true, // Essential cookies are always needed
      analytics: false
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-slate-200 shadow-lg animate-in slide-in-from-bottom duration-300" data-testid="cookie-banner">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 bg-[#101E4D]/10 rounded-lg shrink-0">
              <Cookie className="w-5 h-5 text-[#101E4D]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#101E4D] mb-1">
                {t('cookies.title')}
              </h3>
              <p className="text-sm text-slate-600">
                {t('cookies.message')}{" "}
                <a href="/privacy" className="text-[#FF6B35] hover:underline">
                  {t('cookies.learnMore')}
                </a>
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={declineCookies}
              className="flex-1 md:flex-none text-slate-600 border-slate-300"
              data-testid="cookie-decline-btn"
            >
              {t('cookies.decline')}
            </Button>
            <Button
              size="sm"
              onClick={acceptCookies}
              className="flex-1 md:flex-none bg-[#101E4D] hover:bg-[#0A1330] text-white"
              data-testid="cookie-accept-btn"
            >
              {t('cookies.accept')}
            </Button>
          </div>
          
          <button
            onClick={declineCookies}
            className="absolute top-2 right-2 md:hidden p-1 text-slate-400 hover:text-slate-600"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
